import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ConfirmationService, MessageService } from "primeng/api";
import { BehaviorSubject, Observable, catchError, of, tap, throwError, map } from "rxjs";
import { ApiConstants } from "../../shared/constants/api-constants";
import { CourseConstants } from "../constants/course.constants";
import { Course } from "../models/course";
import { CourseDetail } from "../models/courseDetails";
import { Session } from "../models/session";
import { CourseProficiencyQuiz } from "../models/CourseProficiencyQuiz";
import { sessionAttendeeDTO } from "../models/sessionAttendeeDTO";
import { SessionDetails } from "../models/session-details";
import { SessionAttachmentDetails } from "../models/session-attachment-details";
import { IEQMSScopeLookUpDetails } from '../../shared/models/scopes/eqms-Scope-lookup-details';
import { PrerequisitesData } from "../models/prerequisitesData";
import { EnrollSessionData } from "../models/enroll-session-data";
import { UserListLookup } from "../../shared/models/users/user-list-lookup";
import { SessionAttendeesEnrollDetails } from "../models/session-attendees-enroll-details";
import { NameCode } from "../../shared/models/name-code";
import { CachedHttpService } from "../../../core/caching/cached-http.service";
import { AppliedFilters } from '../../shared-components/panel-hub/models/applied-filters';
import { SortCriteria } from '../../shared-components/panel-hub/models/sort-criteria';
import { SessionQueryResult  } from '../models/session-query-result';
import { SearchMapperService } from '../../shared/services/search-mapper.service';
import { BaseSearchRequest } from '../../shared/models/search/base-search-request';
import { PaginationState } from "../../shared-components/panel-hub/models/pagination-state";

@Injectable({
  providedIn: 'root',
})
export class CourseService {
  courseDetails = {};
  courseLookUpData = {};
  sessionLookUpData = {};
  sessionInternalTrainerLookup: any = [];
  courseProficiencyQuizDetail: CourseProficiencyQuiz = {} as CourseProficiencyQuiz;

  private currentSessionToEdit: SessionDetails | null = null;
  private isSessionEditSlider: boolean = false;
  constructor(private httpClient: HttpClient,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private cachedHttpService: CachedHttpService,
    private searchMapper: SearchMapperService) { }

  editCourseSlider$ = new BehaviorSubject<boolean>(false);
  sessionAttendeeSlider$ = new BehaviorSubject<boolean>(false);
  editCourseMetaDataLoaded$ = new BehaviorSubject<boolean>(false);

  closeAndResetSessionSlider$ = new BehaviorSubject<boolean>(false);
  sessionId$ = new BehaviorSubject<string | null>(null);
  eQMSScopesForCourse: IEQMSScopeLookUpDetails[] = [];


  openConfirmationDialogForSession = () => {
    this.confirmationService.confirm({
      message: CourseConstants.Course_Slider_Confirmation_Text,
      header: CourseConstants.Course_Slider_Confirmation_Header,
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.closeAndResetSessionSlider$.next(false);
      },
    });
  }

  getCourseDetails(): Observable<any> {
    let observable: Observable<any>;
    if (Object.keys(this.courseLookUpData).length === 0) {
      observable = this.httpClient
        .get(ApiConstants.Course_LookUpDetailsApi)
        .pipe(tap((courseData) => {
          (this.courseLookUpData = courseData)
        }));
    } else {
      observable = of(this.courseLookUpData);
    }
    return observable;
  }

  //For internal trainer lookup
  getSessionInternalTrainerData(courseNumber: string): Observable<any> {
    let observable: Observable<any>;
    if (Object.keys(this.sessionInternalTrainerLookup).length === 0) {
      observable = this.httpClient
        .get(ApiConstants.Session_GetInternalTrainer_Api + `${courseNumber}`)
        .pipe(tap((sessionData) => (this.sessionInternalTrainerLookup = sessionData)));
    } else {
      observable = of(this.sessionInternalTrainerLookup);
    }
    return observable;
  }

  ///For session API
  getSessionMasterData(): Observable<any> {
    return this.cachedHttpService.get<any>(ApiConstants.Session_LookUpDetailsApi);
  }

  saveCourse(course: Course): Observable<any> {
    return this.httpClient.post(
      ApiConstants.Course_SaveCourseApi,
      course
    );
  }

  updateCourse(course: Course): Observable<any> {
    return this.httpClient.put(
      ApiConstants.Course_SaveCourseApi, course
    );
  }

  getCourse(
    courseNumber: string,
  ): Observable<CourseDetail> {
    return this.httpClient.get<CourseDetail>(ApiConstants.Course_GetCourseByCourseNumber + `${courseNumber}`);
  }

  saveSessionForCourse(session: Session): Observable<any> {
    return this.httpClient.post(
      ApiConstants.Session_Api,
      session
    );
  }

  getAllSessionManagementDataForCourse(courseNumber: any, isShowAllSelected: boolean): Observable<SessionDetails> {
    return this.httpClient.get<SessionDetails>(ApiConstants.Session_Api + '/' + `${courseNumber}` + '/' + `${isShowAllSelected}`);
  }

  getSessionDetails(
    sessionId: string,
  ): Observable<any> {
    return this.httpClient.get<any>(ApiConstants.Session_Api + '/' + `${sessionId}`);
  }

  getSessionAttendeeDetail(sessionId: string, showAll: boolean): Observable<any> {
    return this.httpClient.get<any>(ApiConstants.SessionAttendees_Api.replace("{sessionId}", sessionId) + showAll);
  }
  updateSessionForCourse(session: Session): Observable<any> {
    return this.httpClient.put(
      ApiConstants.Session_Api + '/' + `${this.sessionId$.value}`,
      session
    );
  }

  cancelSession(sessionId: Session): Observable<any> {
    return this.httpClient.put(
      ApiConstants.Session_Api + "/cancel/" + sessionId, "");
  }

  enrollSession(sessionId: string, enrollSessionData: EnrollSessionData): Observable<any> {
    const url = `${ApiConstants.SessionEnrollment_Api.replace("{sessionId}", sessionId)}`;
    return this.httpClient.put(url, enrollSessionData);
  }

  getInternalTrainers(courseNumber: string): Observable<any> {
    const url = `${ApiConstants.Session_GetInternalTrainer_Api.replace("{courseNumber}", courseNumber)}`;
    return this.httpClient.get(url);
  }

  withdrawSession(sessionId: string, recommendedMaxAttendee: number): Observable<any> {
    return this.httpClient.put(
      ApiConstants.SessionWithdrawn_Api.replace("{sessionId}", sessionId) + recommendedMaxAttendee, "");
  }

  updateSessionAttendee(sessionId: string, sessionAttendees: sessionAttendeeDTO[]): Observable<any> {
    return this.httpClient.put(
      ApiConstants.SessionAttendeesUpdate_Api.replace("{sessionId}", sessionId), sessionAttendees);
  }

  uploadFile(file: File, foldername: string): Observable<any> {
    const formData = new FormData();
    formData.append(file.name, file);
    return this.httpClient.post(
      ApiConstants.Files_Upload_Api + foldername, formData);
  }


  getAttachmentsForSession(sessionId: string): Observable<any> {
    return this.httpClient.get(ApiConstants.GetAttachmentsForSession_Api.replace("{sessionId}", sessionId));
  }

  downloadFile(fileId: string)
    : Observable<any> {
    return this.httpClient.get(ApiConstants.Files_Download_Api + `${fileId}`);
  }

  saveAttachments(sessionId: string, attachmentDetails: SessionAttachmentDetails[]): Observable<any> {
    return this.httpClient.post(ApiConstants.SessionAddAttachments_Api.replace("{sessionId}", sessionId), attachmentDetails);
  }

  deleteSessionAttachments(sessionId: string, deletedAttachmentIds: string[]): Observable<any> {
    return this.httpClient.post(
      ApiConstants.SessionDeleteAttachments_Api.replace("{sessionId}", sessionId),
      deletedAttachmentIds
    );
  }

  //Get Course Proficiency Quiz 
  getCourseProficiencyQuizDetails = (courseNumber?: string): Observable<CourseProficiencyQuiz> => {
    let observable: Observable<CourseProficiencyQuiz>;
    if (Object.keys(this.courseProficiencyQuizDetail).length === 0) {
      observable = this.httpClient
        .get<CourseProficiencyQuiz>(ApiConstants.Course_Proficiency_Quiz_Api + '/' + `${courseNumber}`)
        .pipe(
          tap(courseProficiencyQuizDetail => this.courseProficiencyQuizDetail = courseProficiencyQuizDetail)
        );
    } else {
      observable = of(this.courseProficiencyQuizDetail);
    }
    return observable;
  }

  // Save Course Proficiency Quiz
  saveCourseProficiencyQuiz = (courseProficiencyQuiz: CourseProficiencyQuiz): Observable<CourseProficiencyQuiz> => {
    let observable: Observable<CourseProficiencyQuiz>;
    if (courseProficiencyQuiz.courseProficiencyQuizId === null) {
      observable = this.httpClient
        .post<CourseProficiencyQuiz>(ApiConstants.Course_Proficiency_Quiz_Api, courseProficiencyQuiz)
        .pipe(
          tap(courseProficiencyQuizDetails => this.courseProficiencyQuizDetail = courseProficiencyQuizDetails),
          catchError((err) => {
            this.messageService.add({
              severity: CourseConstants.Severity_Err,
              summary: `${err.error}`,
              life: 20000
            });
            return throwError(() => err);
          })
        );
    } else {
      observable = this.httpClient
        .put<CourseProficiencyQuiz>(ApiConstants.Course_Proficiency_Quiz_Api, courseProficiencyQuiz)
        .pipe(
          tap(courseProficiencyQuizDetails => this.courseProficiencyQuizDetail = courseProficiencyQuizDetails),
          catchError((err) => {
            this.messageService.add({
              severity: CourseConstants.Severity_Err,
              summary: `${err.error}`,
              life: 20000
            });
            return throwError(() => err);
          })
        );
    }
    return observable;
  }
  getTrainingDataForCourse(courseNumber: string): Observable<any> {
    return this.httpClient.post(ApiConstants.Training_Course + `${courseNumber}`, {
      params: {
        courseNumber: courseNumber
      }
    });
  }

  updateTrainingPlanForCourseTrainingItem(courseNumber: string): Observable<any> {
    return this.httpClient.post(ApiConstants.UpdateTraining_Course + `${courseNumber}`, {
      params: {
        courseNumber: courseNumber
      }
    });
  }

  verifyPrerequisitesTrainingStatus(prerequisitesList: PrerequisitesData[]): Observable<any> {
    return this.httpClient.post(ApiConstants.VerifyPrerequisitesData, prerequisitesList);
  }

  // To DO : Need to integrate with file service
  deleteFile(fileId: string): Observable<boolean> {
    return of(true);
  }
  getSessionAttendeeDetails(sessionId: string, attendee: string): Observable<any> {
    return this.httpClient.get(ApiConstants.SessionAttendeesDetails_Api + `${sessionId}/${attendee}`)
  }

  setCurrentSession = (sessionDetails: SessionDetails | null) => {
    this.currentSessionToEdit = sessionDetails
  }

  getCurrentSession = (): SessionDetails | null => {
    return this.currentSessionToEdit;
  }

  setSessionSliderStatus = (isEdit: boolean) => {
    return this.isSessionEditSlider = isEdit;
  }

  getSessionSliderStatus = (): boolean => {
    return this.isSessionEditSlider;
  }

  getCourseCatalogueAuthorisation(): Observable<any> {
    return this.httpClient.get(ApiConstants.Course_GetCourseCatalogueAuthorization);
  }

  geteQMSScopesForCourse = () => {
    let observable: Observable<IEQMSScopeLookUpDetails[]>;
    if (this.eQMSScopesForCourse.length > 0) {
      observable = of(this.eQMSScopesForCourse);
    }
    else {
      observable = this.httpClient.get<IEQMSScopeLookUpDetails[]>(ApiConstants.EQMSScopesLookupForCourse_Api)
        .pipe(
          tap(eQMSScopes => this.eQMSScopesForCourse = eQMSScopes)
        );
    }
    return observable;
  }

  getUsersForSessionAttendeeEnrollement = (sessionId: string): Observable<UserListLookup[]> => {
    return this.httpClient
      .get<UserListLookup[]>(ApiConstants.UsersForSessionAttendeesEnrollment_Api.replace("{sessionId}", sessionId));
  }
  updateSessionAttendeesOnEnrollment = (sessionAttendeesEnrollDetails: SessionAttendeesEnrollDetails)
    : Observable<sessionAttendeeDTO[]> => {
    return this.httpClient
      .post<sessionAttendeeDTO[]>(ApiConstants.SessionAttendeesForEnrollment_Api, sessionAttendeesEnrollDetails)
  }

  getTrainingItemGroupsForCourse(couseNumber: string): Observable<any> {
    return this.httpClient.get<NameCode[]>
      (ApiConstants.Get_TrainingItemGroups_For_Course.replace('{courseNumber}', couseNumber));
  }

  getCourseFromId(courseId: string): Observable<any> {
    const headers = new HttpHeaders().set('X-No-Retry', 'true');
    return this.httpClient.get(ApiConstants.Course_GetCourseByCourseId + `${courseId}`, { headers: headers });
  }

  querySessionManagementData(
    courseNumber: string,
    isShowAllSelected: boolean,
    paginationState : PaginationState,
    filters?: AppliedFilters | null,
    sort?: SortCriteria | null
  ): Observable<SessionQueryResult> {
    const base: BaseSearchRequest = this.searchMapper.mapToBaseSearchRequest(filters, sort, paginationState);
    const payload = {
      CourseNumber: String(courseNumber || ''),
      ShowAll: !!isShowAllSelected,
      ...base
    };
    return this.httpClient.post<SessionQueryResult>(ApiConstants.Session_Search_Api, payload);
  }

}
